import { useDeleteDiscount } from '@api/pos/pos-transaction-api.rtk';
import DataTable from '@components/table/datatable';
import useDataTable from '@hooks/datatable/useDataTable';
import { UseDisclosureInterface } from '@hooks/useDisclosure';
import { ModalWrapper } from '@hooks/useModal';
import useToast from '@hooks/useToast';
import {
  selectPOSTableOrders,
  selectPOSTableTransactionID,
} from '@modules/point-of-sale/slice/pos-selector';
import { Search } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { GridColDef, GridRowId, GridRowSelectionModel } from '@mui/x-data-grid';
import { useAppSelector } from '@store/index';
import { selectPropertyID } from '@store/selector/property.selector';
import { formatNumber } from '@utils/format';
import { useCallback, useMemo, useState } from 'react';
import { usePOSWindowDrawerCtx } from '../pos-order-taking-window';
import { hashSignature } from '@utils/security';
import useAppContext from '@hooks/useAppContext';

const columns: GridColDef[] = [
  { field: 'description', headerName: 'Item', minWidth: 200, flex: 1 },
  { field: 'discountType', headerName: 'Discount Type', width: 140 },
  {
    field: 'quantity',
    headerName: 'Qty',
    width: 70,
    headerAlign: 'center',
    align: 'center',
  },
  // HIDE AMOUNT
  // {
  //   field: 'discountTotal',
  //   headerName: 'Discount',
  //   width: 120,
  //   headerAlign: 'right',
  //   align: 'right',
  //   valueFormatter: ({ value }) => formatNumber(value, 2, 2),
  // },
  // {
  //   field: 'netAmount',
  //   headerName: 'Amount',
  //   width: 120,
  //   headerAlign: 'right',
  //   align: 'right',
  //   valueFormatter: ({ value }) => formatNumber(value, 2, 2),
  // },
];

const POSVoidDiscountModal = ({
  modalDisclosure,
}: {
  modalDisclosure: UseDisclosureInterface;
}) => {
  // #region STATE
  const { openSecCodeDialog } = useAppContext();
  const { toast } = useToast();
  const { othersDrawerDisclosure } = usePOSWindowDrawerCtx();
  const [searchValue, setSearchValue] = useState('');
  const { apiRef, handleSearchValueChange } = useDataTable();
  const transactionID = useAppSelector(selectPOSTableTransactionID);
  const propertyID = useAppSelector(selectPropertyID);
  const orders = useAppSelector(selectPOSTableOrders);

  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);

  const discountedOrders = useMemo(() => {
    if (!orders) return [];

    const seen = new Set<string>();

    return orders
      .filter((item) => item.discountID)
      .filter((item) => {
        if (seen.has(item.orderID)) return false;
        seen.add(item.orderID);
        return true;
      });
  }, [orders]);

  const totalAmount = useMemo(() => {
    if (!discountedOrders || !selectedRows?.length) return 0;

    const selectedSet = new Set(selectedRows);

    return discountedOrders
      .filter((row) => selectedSet.has(row.orderID))
      .reduce((sum, row) => sum + (row.netAmount ?? 0), 0);
  }, [discountedOrders, selectedRows]);
  // #endregion

  // #region FUNC
  const [deleteDiscount, { isLoading: isDeleting }] = useDeleteDiscount();

  const handleSelectionChange = useCallback(
    (selectionModel: GridRowSelectionModel) => {
      setSelectedRows(selectionModel);
    },
    [],
  );

  const handleClose = () => {
    modalDisclosure.onClose();
    setTimeout(() => {
      setSelectedRows([]);
    }, 100);
  };

  const handleVoid = async () => {
    await openSecCodeDialog(async (res) => {
      try {
        if (!selectedRows.length || !res?.data.isValid) return;

        const isAll = selectedRows.length === discountedOrders.length;

        if (isAll) {
          await deleteDiscount({
            transactionID,
            payload: {
              properties: [
                {
                  property: {
                    propertyID,
                    voidDiscount: {
                      discountBy: 'All',
                      voidAll: true,
                      signature: hashSignature(`${propertyID}1`),
                    },
                  },
                },
              ],
            },
          })
            .unwrap()
            .then((response) => {
              toast({
                severity: 'success',
                message: response?.results?.responseMessage,
              });
            });

          return;
        }

        const promises = selectedRows.map((rowId) =>
          deleteDiscount({
            transactionID,
            payload: {
              properties: [
                {
                  property: {
                    propertyID,
                    voidDiscount: {
                      discountBy: 'Reference',
                      discountID: rowId.toString(),
                      signature: hashSignature(`${propertyID}${rowId}0`),
                    },
                  },
                },
              ],
            },
          })
            .unwrap()
            .then((response) => {
              toast({
                severity: 'success',
                message: response?.results?.responseMessage,
              });
            }),
        );

        await Promise.all(promises);
      } catch (error) {
        toast({ message: error, severity: 'warning' });
      } finally {
        handleClose();
        othersDrawerDisclosure.onClose();
      }
    }, 'void_discount');
  };
  // #endregion

  return (
    <ModalWrapper
      {...{
        modalDisclosure,
        withHeader: {
          title: 'Void Discount',
          onClose: handleClose,
        },
      }}
    >
      <Stack sx={{ flex: 1, minHeight: 0 }}>
        {/* CONTENT */}
        <Stack gap={3} sx={{ flex: 1, minHeight: 0 }}>
          {/* CARD */}
          {false && (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              divider={<Divider orientation='vertical' flexItem />}
              gap={2}
              sx={{
                width: '100%',
                '.card': {
                  flex: 1,
                  minWidth: { xs: '300px', md: '200px' },
                },
                '.card-title': {
                  color: '#637381',
                },
                '.card-info': {
                  fontWeight: 500,
                  fontSize: '22px',
                },
              }}
            >
              <Stack className='card'>
                <Typography className='card-title'>Total Order</Typography>
                <Typography className='card-info'>
                  {discountedOrders?.length ?? '-'}
                </Typography>
              </Stack>
              <Stack className='card'>
                <Typography className='card-title'>Selected</Typography>
                <Typography className='card-info'>
                  {selectedRows?.length ?? '-'}
                </Typography>
              </Stack>
              <Stack className='card'>
                <Typography className='card-title'>Total Amount</Typography>
                <Typography className='card-info'>
                  {formatNumber(totalAmount, 2, 2)}
                </Typography>
              </Stack>
            </Stack>
          )}

          {/* SEARCH */}
          {false && (
            <TextField
              value={searchValue}
              size='small'
              placeholder='Search . . .'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='start'>
                    <Search sx={{ color: '#212121' }} />
                  </InputAdornment>
                ),
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleSearchValueChange(e, setSearchValue)
              }
              // disabled={isLoading || isFetching}
            />
          )}

          {/* DATATABLE */}
          <DataTable
            apiRef={apiRef}
            getRowId={(row) => row.discountID}
            columns={columns}
            rows={discountedOrders ?? []}
            sx={{
              p: 0,
              '.MuiDataGrid-selectedRowCount': { display: 'none' },
            }}
            // disableSearch
            // disableColumnFilter
            // disableColumnSelector
            fullHeight
            tableStyle={{
              padding: 0,
              height: 'unset',
            }}
            wrapperStyle={{
              height: 'unset',
              display: 'flex',
              flex: 1,
              minHeight: 0,
            }}
            onRowSelectionModelChange={handleSelectionChange}
            rowSelectionModel={selectedRows}
          />
        </Stack>

        {/* BUTTON */}
        <Stack
          direction='row'
          gap={2}
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: '#FFF',
            justifyContent: 'flex-end',
            borderTop: '1px solid #EEE',
            pt: 3,
          }}
        >
          <Button variant='outlined' onClick={handleClose}>
            Close
          </Button>
          <LoadingButton
            variant='contained'
            disabled={selectedRows?.length <= 0}
            onClick={handleVoid}
            loading={isDeleting}
          >
            Void
          </LoadingButton>
        </Stack>
      </Stack>
    </ModalWrapper>
  );
};

export default POSVoidDiscountModal;
